import type { MutableRefObject } from 'react';

import {
  SearchTeacherListItem,
  SectionInfo,
  TeacherAvailableTime,
  UpdateTrackInfoParamas,
  BaseOption,
} from '@/request/interface/experienceClassBooking';
export type { BaseOption };
import { User } from '@/request/interface/login';
import { GAUserInfo, PixelConfig } from '@/utils/thirdPartyTracking';

export interface SearchFormData {
  language?: string;
  timezone?: string;
  time?: {
    daysOfWeek?: number[];
    timeRanges?: string[];
  };
}

/**
 * 体验课约课流程
 */
export enum ExperienceClassBookingSteps {
  /**
   * 基本问题（学习目标）
   */
  FundamentalQuestionLearningTarget = '1',
  /**
   * 基本问题（学习经验）
   */
  FundamentalQuestionLearningExperience = '2',
  /**
   * 基本问题（年龄）
   */
  FundamentalQuestionAge = '3',
  /**
   * 基本问题（上课时间）
   */
  FundamentalQuestionClassTime = '4',
  /**
   * 筛选上课老师
   */
  FilterTeachers = '5',
  /**
   * 确认选择的老师
   */
  ConfirmTeacher = '6',
  /**
   * 确认老师结果
   */
  ConfirmTeacherResult = '7',
  /**
   * 拓展问题（学生姓名）
   */
  ExtendedQuestionChildName = '8',
  /**
   * 拓展问题（联系语言）- 已废弃
   */
  ExtendedQuestionContactLanguage = '9',
  /**
   * 拓展问题（手机号）- 已废弃
   */
  ExtendedQuestionPhoneNumber = '10',
  /**
   * 扩展问题结果
   */
  ExtendedQuestionResult = '11',
  /**
   * 拓展问题（邮箱）
   */
  ExtendedQuestionEmail = '12',
}

type NextParams<Step extends ExperienceClassBookingSteps, Data = undefined> = {
  step: Step;
} & {
  [key in 'data' as Step extends
    | ExperienceClassBookingSteps.ConfirmTeacherResult
    | ExperienceClassBookingSteps.ExtendedQuestionResult
    ? never
    : key]: Data;
};

type FundamentalQuestionLearningTargetNextParams = NextParams<
  ExperienceClassBookingSteps.FundamentalQuestionLearningTarget,
  Pick<GlobalFormData, 'learningTargets'>
>;

type FundamentalQuestionLearningExperienceNextParams = NextParams<
  ExperienceClassBookingSteps.FundamentalQuestionLearningExperience,
  Pick<GlobalFormData, 'learningExperience'>
>;

type FundamentalQuestionAgeNextParams = NextParams<
  ExperienceClassBookingSteps.FundamentalQuestionAge,
  Pick<GlobalFormData, 'age'>
>;

type FundamentalQuestionClassTimeNextParams = NextParams<
  ExperienceClassBookingSteps.FundamentalQuestionClassTime,
  Pick<GlobalFormData, 'timezone' | 'timeRanges' | 'daysOfWeek'>
>;

type FilterTeachersNextParams = NextParams<
  ExperienceClassBookingSteps.FilterTeachers,
  Pick<GlobalFormData, 'teacher' | 'searchTeacherFormData'>
>;
type ConfirmTeacherParams = NextParams<
  ExperienceClassBookingSteps.ConfirmTeacher,
  Pick<GlobalFormData, 'classTime' | 'confirmTimezone'> & {
    studentSchduleId: string;
  }
>;
type ConfirmTeacherResultParams =
  NextParams<ExperienceClassBookingSteps.ConfirmTeacherResult>;
type ExtendedQuestionChildNameParams = NextParams<
  ExperienceClassBookingSteps.ExtendedQuestionChildName,
  Pick<GlobalFormData, 'childName'>
>;
// 联系语言步骤已废弃
// type ExtendedQuestionContactLanguageParams = NextParams<
//   ExperienceClassBookingSteps.ExtendedQuestionContactLanguage,
//   Pick<GlobalFormData, 'contactLanguage'>
// >;
// 手机号步骤已废弃
// type ExtendedQuestionPhoneNumberParams = NextParams<
//   ExperienceClassBookingSteps.ExtendedQuestionPhoneNumber,
//   Pick<GlobalFormData, 'phoneNumber' | 'countryCode'>
// >;
type ExtendedQuestionEmailParams = NextParams<
  ExperienceClassBookingSteps.ExtendedQuestionEmail,
  Pick<GlobalFormData, 'email'>
>;
type ExtendedQuestionResultParams =
  NextParams<ExperienceClassBookingSteps.ExtendedQuestionResult>;

/**
 * 下一步操作
 */

export type NextStep = (
  value:
    | FundamentalQuestionLearningTargetNextParams
    | FundamentalQuestionLearningExperienceNextParams
    | FundamentalQuestionAgeNextParams
    | FundamentalQuestionClassTimeNextParams
    | FilterTeachersNextParams
    | ConfirmTeacherParams
    | ConfirmTeacherResultParams
    | ExtendedQuestionChildNameParams
    | ExtendedQuestionEmailParams
    | ExtendedQuestionResultParams,
) => void;

export type LoginTeacher = Pick<
  SearchTeacherListItem,
  'wkHeadImageUrl' | 'uuid' | 'nameEn'
>;
/**
 * 全局表单数据
 */
export interface GlobalFormData {
  /**
   * 选择的学习目标
   * @example "A,B,C,D" 逗号分割的字符串
   */
  learningTargets?: string;
  /**
   * 选择的学习经验
   */
  learningExperience?: string;
  /**
   * 选择的年龄
   */
  age?: number;
  /**
   * 选择的时区
   */
  timezone?: string;
  /**
   * 周几；注意： 0 代表周日
   */
  daysOfWeek?: number[];
  /**
   * 时间范围 eg: ['8-10', start-end],
   */
  timeRanges?: string[];
  /**
   * 选择的老师
   */
  teacher?: SearchTeacherListItem;
  /**
   * 预约的上课时间
   */
  classTime?: TeacherAvailableTime;
  /**
   * 填写的学生姓名
   */
  childName?: string;
  /**
   * 填写的联系语言 - 已废弃
   */
  contactLanguage?: string;
  /**
   * 填写的邮箱
   */
  email?: string;

  /**
   * 老师列表筛选项
   */
  searchTeacherFormData?: SearchFormData;
  /**
   * 确认约课的时区
   */
  confirmTimezone?: string;
}
/**
 * 约课全局状态
 */
export interface ExperienceClassBookingState {
  /**
   * 当前步骤
   */
  currentStep?: ExperienceClassBookingSteps;

  /**
   * 上一步
   */
  previousStep?: () => void;

  /**
   * 下一步
   */
  nextStep: NextStep;

  timezoneList: BaseOption[];
  countryList: BaseOption[];
  /**
   * 全局表单数据
   */
  globalFormData?: GlobalFormData;

  /**
   * cloudflare 校验相关
   */
  cloudFlareState?: CloudFlareState;
  /**
   * 是否登录
   */
  isLogin: boolean;
  /**
   * 登录账号信息
   */
  userInfo?: User;

  handleUpdateTrackInfo: (e: UpdateTrackInfoParamas) => Promise<void>;
  /**
   * 登录关联老师（进线老师）
   */
  loginTeacher?: LoginTeacher;
  /**
   * 设置登录关联老师
   */
  setLoginTeacher: (loginTeacher: LoginTeacher) => void;
  /**
   * 是否初始化完成
   */
  isInited: boolean;
  /**
   * 更新约课状态
   */
  validateBookStatus: () => void;

  /**
   * 约课课节信息(登录后有)
   */
  sectionInfo?: SectionInfo;
  trace: (e: string, data?: TraceData) => void;
  /**
   * 页面是否出现滚动条
   */
  hasPageScroll: boolean;
  /**
   * 是否为改约模式
   */
  isRescheduleMode?: boolean;
  /**
   * 统一约课错误处理
   * @returns 是否可以继续约课（true 表示可以，false 表示不能）
   */
  handleBookingError?: (params: BookingErrorParams) => Promise<boolean>;
  /**
   * 是否已完成登录后的检查（GS2 打标 + checkCanBook）
   * 用于避免 handleLoginSuccess 和 FilterTeachers 重复检查
   */
  hasLoginCheckedRef?: MutableRefObject<boolean>;
  /**
   * 当前学员 ID
   */
  currentStudentId?: string | null;
  /**
   * 线索ID
   */
  l?: string;
  // ========== GA/FB 埋点相关 ==========
  /**
   * Pixel 配置（从 l 参数获取）
   */
  pixel?: PixelConfig | null;
  /**
   * 进线来源（从 createGSUserLead 返回）
   */
  leadSource?: string;
  /**
   * 设置进线来源
   */
  setLeadSource?: (source: string) => void;
  /**
   * 埋点 trackId（从 createGSUserLead 返回，FB 埋点用）
   */
  trackId?: string;
  /**
   * 设置 trackId
   */
  setTrackId?: (id: string) => void;
  /**
   * 获取 GA 埋点需要的用户信息
   */
  getUserInfo?: () => GAUserInfo;
}

export interface TraceData {
  [key: string]: any;
}

/**
 * 重复约课展示参数
 */
export interface ReBookingViewInfo {
  classTime?: TeacherAvailableTime;
  timezone?: string;
  // 优先展示
  timezoneLabel?: string;
  teacherName?: string;
  teacherAvatar?: string;
  scheduleName?: string;
}

type CloudFlareStatus = 'success' | 'failed' | 'pending';
/**
 * cloudflare 校验相关
 */
export interface CloudFlareState {
  token?: string | null;
  status: CloudFlareStatus;
  clearToken: () => void;
}

/**
 * 修改某个属性为必需
 */
export type MakePropertyRequired<T, K extends keyof T> = {
  [key in K]-?: T[key];
} & Omit<T, K>;

export interface ExperienceClassBookingProps {
  currentStep: ExperienceClassBookingSteps;
  targetStep?: string;
  l?: string;
  trigger_pvid?: string;
  trigger_name?: string;
  source?: string;
  bookingMode?: 'RESCHEDULE';
  triggerPvid?: string;
}

// ==================== 约课错误相关类型 ====================

/**
 * 约课错误类型
 * 注：中文体验课不涉及付费场景，不需要 EXIST_ORDER 类型
 */
export type BookingErrorType =
  | 'LIMITED' // 达到约课次数限制
  | 'UNAVAILABLE' // 无剩余班容/时间不可用/非GS2用户
  | 'CONFLICT' // 学生在该时间段有冲突的课程
  | 'SCHEDULE_FULL' // 班容已满
  | 'COMING_SOON' // 超时
  | 'ALREADY' // 学生已有该科目的体验课（已约课，显示已约课程信息）
  | 'OTHER'; // 其他错误

/**
 * 冲突课程信息（ALREADY 错误时返回）
 * 当学生已约过该科目的体验课时，接口返回已约课程的详细信息
 */
export interface ConflictSchedule {
  /** 学员ID */
  studentId?: string;
  /** 老师ID */
  teacherId?: string;
  /** 老师名称 */
  teacherName?: string;
  /** 老师英文名 */
  teacherNameEn?: string;
  /** 老师头像URL */
  teacherContentUrl?: string;
  /** 课程排期ID */
  courseScheduleId?: string;
  /** 课节名称 */
  sectionName?: string;
  /** 课节英文名称 */
  sectionNameEn?: string;
  /** 学员端课节名称 */
  sectionNameStudent?: string;
  /** 学员端课节英文名称 */
  sectionNameStudentEn?: string;
  /** 上课开始时间 */
  startDateTime?: string;
  /** 上课结束时间 */
  endDateTime?: string;
  /** 中国时区开始时间 */
  chinaStartDateTime?: string;
  /** 中国时区结束时间 */
  chinaEndDateTime?: string;
  /** 时区 */
  timeZone?: string;
  /** 班型：GROUP(小班课) / ONE_ON_ONE(1v1) */
  classType?: 'GROUP' | 'ONE_ON_ONE' | string;
  /** 课程时长(分钟) */
  duration?: number;
  /** 状态 */
  status?: string;
  /** 是否GS2 */
  gs2?: boolean;
  /** 创建时间 */
  whenCreated?: string;
}

/**
 * 约课错误处理参数
 */
export interface BookingErrorParams {
  /** 错误类型（如果有则直接显示对应弹窗，否则调用接口检查） */
  errorType?: BookingErrorType;
  /** 错误消息 */
  errorMsg?: string;
  /** 冲突课程信息（ALREADY 类型时返回） */
  conflictSchedule?: ConflictSchedule;
  /** 学员 ID（用于登录后立即约课场景，避免 React state 异步更新问题） */
  studentId?: string;
  /** 是否为改约模式 */
  reschedule?: boolean;
  /** 老师ID */
  teacherId?: string;
}

/**
 * 约课错误状态
 */
export interface BookingErrorState {
  /** 是否显示错误弹窗 */
  showErrorModal: boolean;
  /** 错误类型 */
  errorType: BookingErrorType | null;
  /** 冲突课程信息（ALREADY 类型时使用） */
  conflictSchedule?: ConflictSchedule | null;
}
